import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Mail, Phone, Send } from "lucide-react";
import { FadeUp, Eyebrow, SectionShell, EASE } from "./Reveal";
import { analytics } from "../lib/analytics";
import { COMPANY } from "../constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "US Bookkeeping",
  "US Taxation (1040 / 1065 / 1120 / 1120-S)",
  "Payroll Processing",
  "Sales & Use Tax",
  "Virtual CFO / MIS",
  "Dedicated Resource",
  "Other / Not sure yet",
];

const inputCls =
  "w-full rounded-xl border border-line bg-paper px-4 py-3.5 text-sm text-ink placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-torange/60 focus:ring-2 focus:ring-torange/20 focus:bg-white";

export default function Contact() {
  const [form, setForm] = useState({ name: "", firm: "", email: "", phone: "", service: SERVICE_OPTIONS[0], message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form, { timeout: 15000 });
      analytics.contactSubmitted("website_form");
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <SectionShell id="contact" className="py-24 sm:py-32">
      <div className="relative overflow-hidden rounded-[2.5rem] border border-line bg-card shadow-soft">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-bluedark/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-torange/10 blur-3xl" />

        <div className="relative grid lg:grid-cols-2 gap-12 p-7 sm:p-12 lg:p-16">
          <div>
            <FadeUp>
              <Eyebrow>Start a conversation</Eyebrow>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] text-ink">
                Book your <span className="text-gradient">discovery call.</span>
              </h2>
              <p className="mt-6 text-slate-500 leading-relaxed max-w-md">
                Tell us what's on your desk — we'll reply with scope, timeline
                and indicative pricing. No obligation, full confidentiality.
              </p>
            </FadeUp>

            <FadeUp delay={0.15} className="mt-10 space-y-4">
              <a
                data-testid="contact-email-link"
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-4 rounded-2xl border border-line bg-paper px-5 py-4 hover:border-bluze/50 transition-colors duration-300 group"
              >
                <Mail size={19} className="text-bluze" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Email</p>
                  <p className="text-sm font-semibold text-ink group-hover:text-bluedark">{COMPANY.email}</p>
                </div>
              </a>
              <a
                data-testid="contact-phone-link"
                href={COMPANY.phoneHref}
                className="flex items-center gap-4 rounded-2xl border border-line bg-paper px-5 py-4 hover:border-torange/50 transition-colors duration-300 group"
              >
                <Phone size={19} className="text-torange" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Call / WhatsApp</p>
                  <p className="text-sm font-semibold text-ink group-hover:text-torange">{COMPANY.phone}</p>
                </div>
              </a>
            </FadeUp>
          </div>

          <FadeUp delay={0.1}>
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  data-testid="contact-form-success"
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="h-full min-h-[420px] flex flex-col items-center justify-center text-center rounded-3xl border border-emerald-500/30 bg-emerald-500/5 p-10"
                >
                  <CheckCircle2 size={56} className="text-emerald-500" />
                  <h3 className="mt-6 font-display text-2xl font-bold text-ink">Enquiry received.</h3>
                  <p className="mt-3 max-w-xs text-sm text-slate-500">
                    Thank you — our team will get back to you shortly at the email you provided.
                  </p>
                  <button
                    data-testid="contact-form-reset"
                    onClick={() => { setForm({ name: "", firm: "", email: "", phone: "", service: SERVICE_OPTIONS[0], message: "" }); setStatus("idle"); }}
                    className="mt-8 rounded-full border border-ink/15 px-6 py-3 text-xs font-bold text-slate-600 hover:text-ink hover:border-torange/50 transition-all cursor-pointer"
                  >
                    Send another enquiry
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="contact-form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-4 rounded-3xl"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input required data-testid="contact-input-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                    <input data-testid="contact-input-firm" className={inputCls} placeholder="Firm name" value={form.firm} onChange={set("firm")} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input required type="email" data-testid="contact-input-email" className={inputCls} placeholder="Work email *" value={form.email} onChange={set("email")} />
                    <input type="tel" data-testid="contact-input-phone" className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} />
                  </div>
                  <select data-testid="contact-input-service" className={`${inputCls} cursor-pointer`} value={form.service} onChange={set("service")}>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s} className="bg-white text-ink">{s}</option>
                    ))}
                  </select>
                  <textarea data-testid="contact-input-message" rows={4} className={`${inputCls} resize-none`} placeholder="What do you need off your desk?" value={form.message} onChange={set("message")} />
                  {status === "error" && (
                    <p data-testid="contact-form-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-500">
                      Something went wrong sending your enquiry — please try again, or reach us directly at {COMPANY.email}.
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="contact-form-submit-button"
                    disabled={status === "loading"}
                    className="group inline-flex w-full items-center justify-center gap-2.5 rounded-xl bg-gradient-to-r from-bluedark via-bluze to-torange bg-[length:150%_100%] bg-left hover:bg-right px-8 py-4 text-sm font-bold text-white shadow-lift transition-all duration-500 disabled:opacity-60 cursor-pointer"
                  >
                    {status === "loading" ? (
                      <Loader2 size={17} className="animate-spin" />
                    ) : (
                      <Send size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                    )}
                    {status === "loading" ? "Sending…" : "Send Enquiry"}
                  </button>
                  <p className="text-center text-[11px] text-slate-400">Your details stay strictly confidential.</p>
                </motion.form>
              )}
            </AnimatePresence>
          </FadeUp>
        </div>
      </div>
    </SectionShell>
  );
}
